import { useSearchIgCreators } from "components/MediaLibrary/LibrarySidebar/SourceMediaSidebar/CreatorSidebar/UserSelector";
import { GET_CREATOR_MEDIA_ROUTINE } from "constants/ActionTypes";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRoutinesLoading } from "selectors/commonSelectors";
import {
  getFeedCreatorUsername,
  getFeedMedia,
  getSelectedIgAccount,
} from "selectors/hashtagFeedSelectors";
import { LoadingCover } from "ui/cover";
import { CollectionFilesList } from "../../collection-files-list";
import { PickerProps } from "../media-picker";
import { Title, TitleBar } from "../components/common";
import { FeedMedia, InstagramUser } from "../types";
import { feedMediaToCollectionFile } from "../helper";
import { AccountSelector } from "../components/account-selector";
import { CreatorSelector } from "./creator-selector";
import { PAGES } from "constants/MediaLibrary";
import { LoadMore } from "../components/load-more";
import { initCreatorFeed } from "actions/sourceMedia";
import { getUser } from "selectors/commonSelectors";
import { hasUgcLibrary } from "utils/feature-gates";
import styled from "styled-components";
import { useOpenIntercomChat } from "libs/use-open-intercom-chat";
import { goToManagePlan } from "utils/manage-plan";
import { useTrackEvent } from "libs/analytics";
import { UPGRADE_LIBRARY_VIEWED } from "constants/Events";
import { Button } from "@/components/ui/button";
import { UpsellRocketIcon } from "@/components/ui/icon";
import type { ReduxRootState } from "types/redux-store";

export function CreatorPicker(props: PickerProps) {
  const dispatch = useDispatch();
  const {
    isLoading: isLoadingCreators,
    requestCreator,
    recentCreators,
    selectedCreator,
    handleRemoveCreator,
  } = useSearchIgCreators();

  const selectedIgAccount = useSelector(getSelectedIgAccount);
  const creatorUsername = useSelector(getFeedCreatorUsername) as string;

  const feedMedia = useSelector((state: ReduxRootState) =>
    getFeedMedia(state, PAGES.CREATOR)
  ) as FeedMedia[];
  const files = feedMedia.map(feedMediaToCollectionFile);

  const loadingInitialMedia = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [GET_CREATOR_MEDIA_ROUTINE])
  );
  const isLoading = loadingInitialMedia || isLoadingCreators;

  const onCreatorSelected = React.useCallback(
    (user: InstagramUser) => {
      if (!selectedIgAccount?._id) {
        return;
      }
      dispatch(initCreatorFeed({ user, accountId: selectedIgAccount._id }));
    },
    [selectedIgAccount]
  );

  React.useEffect(() => {
    if (!isLoading && !selectedCreator && recentCreators.length > 0) {
      onCreatorSelected(recentCreators[0]);
    }
  }, [isLoading, selectedCreator, recentCreators, onCreatorSelected]);

  const user = useSelector(getUser);
  const upsell = !hasUgcLibrary(user);

  if (upsell) {
    return (
      <UpsellWrap>
        <strong className="head">
          Save content by brand partners, influencers and advocates.
        </strong>
        <strong>1. Search a creator's Instagram account</strong>
        <strong>2. Choose posts to save</strong>
        <p>
          The original creators' info is automatically saved so you can pass on
          the credit
        </p>
        <strong>3. Add it to your Library</strong>
        <strong>
          4. Share it across Instagram, Facebook, Twitter, Pinterest and
          LinkedIn
        </strong>

        <UpsellUGCCTA />
      </UpsellWrap>
    );
  }

  return (
    <LoadingCover isLoading={isLoading}>
      <div className="flex items-center gap-3 my-5">
        <AccountSelector type="creator" />
        <CreatorSelector
          requestUser={requestCreator}
          recentUsers={recentCreators}
          onCreatorSelected={onCreatorSelected}
          handleRemoveCreator={handleRemoveCreator}
        />
      </div>
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
          <p className="font-inter text-sm text-neutral-600">
            Loading creator posts...
          </p>
        </div>
      ) : selectedIgAccount && feedMedia.length === 0 ? (
        <EmptyAlert />
      ) : (
        <>
          <TitleBar>
            <Title>{creatorUsername}</Title>
          </TitleBar>
          <CollectionFilesList
            files={files}
            selectedMediaSet={props.selectedMediaFiles}
            selectedCollection={props.selectedCollection}
            onSelect={props.handleFileAdd}
            onRemove={props.handleFileRemove}
          />
          <LoadMore page={PAGES.CREATOR} />
        </>
      )}
    </LoadingCover>
  );
}

function EmptyAlert() {
  return (
    <div className="text-center py-12 max-w-md mx-auto">
      <i className="fa-regular fa-users text-4xl text-neutral-300 mb-4"></i>
      <h3 className="font-gotham font-semibold text-lg mb-2 text-neutral-900">
        Search for influencers and creators
      </h3>
      <p className="font-inter text-sm text-neutral-600 mb-4">
        Source content from brand partners, influencers, and business accounts
        you work with.
      </p>
      <ol className="text-left inline-block font-inter text-sm space-y-2 text-neutral-700">
        <li>1. Select an Instagram account above</li>
        <li>2. Search for and add a creator</li>
        <li>3. Browse their posts and select content</li>
      </ol>
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="font-inter text-xs text-blue-700">
          <i className="fa-regular fa-circle-info mr-1"></i>
          Ensure you have permission to repost content from creators!
        </p>
      </div>
    </div>
  );
}

export function UpsellUGCCTA({
  hideSupportLink,
}: {
  hideSupportLink?: boolean;
}) {
  const openIntercomChat = useOpenIntercomChat();
  const trackEvent = useTrackEvent();

  return (
    <UpsellUGCCTAWrap>
      <UpsellButtonWrap>
        <Button
          variant="blue-primary"
          size="md"
          onClick={() => {
            trackEvent({
              eventName: UPGRADE_LIBRARY_VIEWED,
              useServices: ["AMP", "HS"],
            });
            goToManagePlan();
          }}
        >
          Add Library to your plan
          <UpsellRocketIcon />
        </Button>
      </UpsellButtonWrap>
      {!hideSupportLink && (
        <p className="help">
          Get help from
          <a
            onClick={() => {
              openIntercomChat();
            }}
          >
            {" "}
            Sked Support
          </a>
        </p>
      )}
    </UpsellUGCCTAWrap>
  );
}

export const UpsellWrap = styled.div`
  padding: 20px 0;
  line-height: 2;

  .head {
    margin-left: 12px;
  }

  strong {
    display: block;
  }

  p {
    font-size: 14px;
    margin-bottom: 8px;
    margin-left: 18px;
  }
`;

const UpsellUGCCTAWrap = styled.div`
  text-align: center;
`;

const UpsellButtonWrap = styled.div`
  margin: 15px auto 10px;
`;
